// The map color scale, shared by the server (/api/map sends `colors` and level-wide `stops`) and the web app
// (which re-fits the stops to the areas on screen). Pure, no DOM.

import type { MetricDef } from "./types.ts";

/**
 * 11-stop blue → pale → red ramp (low → high), in the spirit of Reventure's map.
 * Continuous: MapLibre interpolates between stops, so neighbouring areas get distinct shades.
 */
const BLUE_RED = [
  "#1f3bff", "#4e63ff", "#8088ff", "#aeb0ff", "#d6d4fa", "#f1e6ee",
  "#f9cfd0", "#f7a7a2", "#f27a70", "#e8483c", "#d4140f",
];

/** Colors low → high. Metrics where high is good for buyers run red → blue so "good" always reads blue. */
export function ramp(m: Pick<MetricDef, "higher">): string[] {
  return m.higher === "good" ? [...BLUE_RED].reverse() : BLUE_RED;
}

function quantile(sorted: number[], q: number): number {
  if (!sorted.length) return Number.NaN;
  const pos = (sorted.length - 1) * q;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  return sorted[lo]! + (sorted[hi]! - sorted[lo]!) * (pos - lo);
}

/**
 * 11 increasing stop values for the given values.
 * Sequential metrics: equal-count stops (2nd, 10th, 20th … 90th, 98th percentile), so color spreads evenly
 * across the areas being compared. Diverging metrics: symmetric around the metric's center (0 or 50).
 */
export function computeStops(m: Pick<MetricDef, "scale" | "center">, values: number[]): number[] {
  const v = values.filter((x) => Number.isFinite(x)).sort((a, b) => a - b);
  if (!v.length) return [];
  let stops: number[];
  if (m.scale === "diverging") {
    const c = m.center ?? 0;
    const span = Math.max(Math.abs(quantile(v, 0.02) - c), Math.abs(quantile(v, 0.98) - c)) || 1;
    stops = Array.from({ length: 11 }, (_, i) => c + span * (-1 + i / 5));
  } else {
    stops = [0.02, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 0.98].map((q) => quantile(v, q));
  }
  // interpolate needs strictly increasing inputs
  const eps = (Math.abs(stops[10]! - stops[0]!) || Math.abs(stops[0]!) || 1) * 1e-6;
  for (let i = 1; i < stops.length; i++) if (stops[i]! <= stops[i - 1]!) stops[i] = stops[i - 1]! + eps;
  return stops;
}
